/**
 * intake-form.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Todd L Smith <tsmith@safetyweb.org>
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild }
  from "@angular/core";
import { AbstractControl, FormGroup, FormControl } from "@angular/forms";
import {
  ActivatedRoute,
  Event as RouterEvent,
  NavigationEnd,
  Router,
  UrlSegment
} from "@angular/router";
import { DialogRef, ModalComponent } from "angular2-modal";
import { Modal } from "angular2-modal/plugins/bootstrap";
import { Observable, Subscription } from "rxjs/Rx";

import { UserService } from "../auth/identity/user.service";
import { AlertService } from "../common/alert.service";
import { ReferSubjectContextRequest }
  from "../server/messages/context-management/";
import { GENERIC_REASON_TEXT, ResponseCode } from "../server/messages/general/";
import { MessagingService } from "../server/messaging.service";
import { SubjectService } from "../subjects/subject.service";
import { ChatPanelComponent } from "./chat-panel.component";
import { CommentService } from "./comment.service";
import { ExportModalComponent } from "./export-modal.component";
import { ReferralModalComponent }
  from "./referral-modal.component";
import { SaveableForm } from "./saveable-form";

import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { IntakeStepService }
  from "../algorithms/management/intake-step.service";
import { Algorithm, AlgorithmType } from "../algorithms/structure/algorithm";
import { RunningAlgorithm } from "../algorithms/structure/running-algorithm";
import { Subject } from "../subjects/subject";
import { SubjectReference } from "../subjects/subject-reference";
import { ModalHelpers } from "../support/utilities";
import { oneline, textualList } from "../support/utilities/functions";
import { anyChildHasError, CustomValidators } from "../support/validators";
import { WatchlistService } from "../watchlist/watchlist.service";
import { AnswerProxy } from "../algorithms/management/answer-proxy";
import { IntakeStep, ReportStep } from "../algorithms/structure";
import { SettingService } from "app/configuration/setting.service";
import { ReportStepService } from "app/algorithms/management/report-step.service";
import { SearchService, Result } from "app/search/search.service";
import { ModalService, ConfirmationContext } from "app/common/modal.service";
import { AddReminderModalComponent, ReminderModalResult } from "app/watchlist/add-reminder-modal.component";

declare const window : Window &
  {requestIdleCallback : (cb : () => void) => number};

type ControlPairs = Array<[string, AbstractControl]>;

const SAVE_NEEDED_MODAL :
  (d : ControlPairs, c : boolean) => ConfirmationContext
  = (dirties : ControlPairs, commentChanges : boolean) => (
  {
    title: "Save subject data?",
    yesLabel: "Save & Continue",
    noLabel: "Stay",
    cancelLabel: "Leave anyway",
    body: `
    <p>
      It looks like you may have entered data since the last save. (This could
      include changes you have made to comments.)
    </p>
    <p>
      If you leave this page, unsaved data will be lost.
      Would you like to save?
    </p>
    <hr>
    <label>
    These values appear to be different from the last database version:
    </label>
    <ul>
    ${dirties.map(
      ([name] : [string, any]) => `<li>${name}</li>`).join("\n")}
    ${commentChanges ? "<li><em>(1 or more comments)</em></li>" : ""}
    </ul>`
    // no dismiss value (we need empty obs to distinguish "3rd option")
});
const BROWSER_SAVE_NEEDED_MESSAGE : string = `
  It looks like you may have entered data since the last save. (This could
  include changes you have made to comments.)

  Are you sure you want to leave?
`;

const REQUIRED_STEPS_MODAL : ConfirmationContext = {
  title: "Required steps not complete",
  body: `You must fill out all of the required steps before saving!`
};

const LEAVING_UNSAVABLE_MODAL : ConfirmationContext = {
  title: "Required steps not complete",
  body: `If you leave, this subject will not be saved. (You cannot save
        until you enter all of the required steps.)`,
  yesLabel: "Stay & edit",
  cancelLabel: "Leave anyway",
  dismissValue: false
};

const MAG_ICON : string =
  `<span class="glyphicon glyphicon-search"><span>`;

const RETRIEVABILITY_MODAL : ConfirmationContext = {
  title: "Subject would not be retrievable!",
  body: `Since you haven't completed enough of the search index steps
        (marked with a <kbd>${MAG_ICON}</kbd>), this record would not be
        retrievable through all searches. Would you like to save anyway?`
};

const INCOMPLETE_COMMENTS_MODAL : ConfirmationContext = {
  title: "Unsubmitted comment changes",
  body: `Some of your comments hadn't yet been sent to the server when you
        tried to leave. To ensure they were successfully submitted, it may be
        necessary to wait a moment and save again.
        <br><br>
        <em>
        (If you see this message repeatedly, please report it as an issue.)
        </em>`,
  yesLabel: "Stay & edit",
  cancelLabel: "Leave anyway",
  dismissValue: false
};

@Component({
  selector: "coverage-form",
  template: `
    <div class="form-flow" [class.fullwidth]="!useSidebar">
    <div class="row">
      <div
      [class.col-sm-8]="useSidebar"
      [class.defocus]="sidebarOpen"
      class="question-column">
        <!-- header -->
        <!-- <span class="debug-only">lastRefURL: {{algorithm?.subject?.lastRefURL}}</span> -->
        <a
          *ngIf="algorithm?.subject?.newlyCreated !== true && !algorithm?.subject?.lastRefURL"
          class="nonprint"
          [routerLink]="['/subject', objectId]">
          &lt; back to summary
        </a>
        <a
          *ngIf="algorithm?.subject?.newlyCreated !== true && algorithm?.subject?.lastRefURL"
          class="nonprint"
          [routerLink]="algorithm?.subject?.lastRefURL">
          &lt; back to worklist
        </a>
        <a
          *ngIf="algorithm?.subject?.lastRefType == 'search'"
          class="pull-right text-warning"
          (click)="openNextSearchResult()">
          open next search result &gt;
        </a>
        <!-- <span class="badge" style="opacity: 40%">{{algorithm?.subject?.lastRefType}}</span> -->
        <h2
          class="intake-header"
          [class.has-comment]="algorithm?.subject?.comments.length > 0">
          {{title}}
          <small
            class="subject-timestamp"
            [title]="algorithm?.subject?.created | date : 'medium'">
            created {{algorithm?.subject?.created | date}}
          </small>

          <!-- comment list opener -->
          <a
            (click)="menu.open = menu.open ? false : true" class="menu-button comment-crumb">
            <span class="glyphicon glyphicon-comment"></span>
            <span class="badge">
              {{algorithm?.subject?.comments.length || 0}}
            </span>
          </a>
        </h2>

        <!-- comments directly on the subject context itself -->
        <div class="menu" #menu [class.open]="menu?.open">
            <comment-list
              [comments]="algorithm?.subject?.comments"
              [parent]="algorithm?.subject">
            </comment-list>
        </div>

        <quick-links></quick-links>

        <!-- the actual intake step list (the form body) -->
        <intake-step-list [form]="form" (keyup)="substitutionMacro($event)" [ngClass]="algorithm?.name || 'a-NONE'"></intake-step-list>

        <div class="end-buttons w100">
          <div [class.col-sm-6]="useReturnHome" [class.col-sm-4]="useFCWIButtons_DEBUG">
            <button
              (click)="scrollToTop()"
              class="form-control btn btn-default">
              <span class="glyphicon glyphicon-arrow-up"></span>
              Return to Top
            </button>
          </div>
          <div *ngIf="useReturnHome" class="col-sm-6">
            <button
              (click)="trySaveSubject(true)"
              class="form-control btn btn-success">
              <span class="glyphicon glyphicon-floppy-disk"></span>
              Submit Form and Return Home
            </button>
          </div>
          <div *ngIf="useFCWIButtons_DEBUG" class="col-sm-4">
            <button
              (click)="printPage()"
              class="form-control btn btn-primary">
              <span class="glyphicon glyphicon-print"></span>
              Print Your Results
            </button>
          </div>
          <div *ngIf="useFCWIButtons_DEBUG" class="col-sm-4">
            <a
              href="https://forms.gle/KdxGVAvUuhTvCxj8A"
              title="This will open a survey about your experience with FirstCheck WI"
              target="_blank"
              class="form-control btn btn-warning">
              <span class="glyphicon glyphicon glyphicon-list-alt"></span>
              Take Survey
            </a>
          </div>
        </div>
      </div>

      <div *ngIf="sidebarOpen" class="backdrop" (click)="sidebarOpen = false">
      </div>
      <!-- SIDEBAR CONTENT -->
      <div *ngIf="useSidebar" class="details-bar" [class.in]="sidebarOpen">
        <button
          class="btn btn-default toggle visible-xs-inline-block nonprint"
          (click)="sidebarOpen = !sidebarOpen">
          {{ sidebarOpen ? "Close Actions" : "Actions Menu" }}
        </button>
        <div class="contents">
          <div class="row">
          <div class="quick-action-button col-xs-12 col-md-6">
            <button
              (click)="trySaveSubject()"
              class="btn btn-success "
              [disabled]="saveRequested">
              <span class="glyphicon glyphicon-floppy-disk pull-left"></span>
              Save <!-- TODO: progress indication -->
            </button>
          </div>
          <div class="quick-action-button col-xs-12 col-md-6">
            <button
              (click)="tryDuplicate()"
              class="btn btn-default "
              [disabled]="false">
              <span class="glyphicon glyphicon-duplicate pull-left"></span>
              Duplicate
            </button>
          </div>
          </div>
          <div class="row">
          <div class="quick-action-button col-xs-12 col-md-6">
            <watch-button
              [subject]="algorithm?.subject"
              [disabled]="algorithm?.subject?.newlyCreated">
            </watch-button>
          </div>
          <div class="quick-action-button col-xs-12 col-md-6">
            <button
              (click)="showLinkedReminderModal()"
              class="btn btn-default"
              [disabled]="false">
              <span class="glyphicon glyphicon-calendar pull-left"></span>
              Reminder...
            </button>
          </div>
          </div>
          <div class="row">
          <!-- <div class="quick-action-button">
            <select
              class="form-control"
              [formControl]="actionSelection"
              (change)="advancedAction($event)">
              <option [ngValue]="null" disabled>— Advanced Actions —</option>
              <option
                [disabled]="algorithm?.subject?.newlyCreated"
                value="refer">
                Refer to {{algorithms.referralOrganization}}...
              </option>
              <option disabled>
                Change Group Visibility...
              </option>
              <option disabled>
                Request Deletion...
              </option>
              <option value="export">
                Export to File...
              </option>
              <option value="import">
                Import from File...
              </option>
            </select>
          </div> -->
          <div class="quick-action-button col-xs-12 col-md-6">
            <button
              class="btn btn-danger "
              [disabled]="true">
              <span class="glyphicon glyphicon-trash pull-left"></span>
              Request Deletion
            </button>
          </div>
          <div class="quick-action-button col-xs-12 col-md-6">
            <select
              class="form-control"
              [formControl]="algSwitcher"
              (change)="switchToAlg()">
              <option [ngValue]="null" disabled>Switch view...</option>
              <option
                *ngFor="let alg of algorithms.bySCType(algorithm.algorithm.contextType)"
                [disabled]="alg == algorithm.algorithm"
                [ngValue]="alg">
                {{alg.displayName}}
              </option>
            </select>
          </div>
          </div>
          <collaborator-panel
            openOnChanges="true"
            startOpen="false">
          </collaborator-panel>
          <chat-panel #chat></chat-panel>
          <report-panel></report-panel>
          <relationship-panel [subject]="algorithm.subject">
          </relationship-panel>
        </div>
      </div>
    </div> <!-- / .row -->
    </div> <!-- / .form-flow -->
`,
  styles: [
    `.form-flow {
      /* padding-left: 8%; */
      max-width: 1500px;
      margin: auto 30px;
    }
    /deep/ .a-first-check-wi .form-flow {
      max-width: 800px;
      /* padding-left: 0%; */
      margin: auto;
    }`,
    `.question-column { padding-left: 50px; }`, /* TODO: this should have better interaction with margins, kinda sketchy rn */
    `.details-bar {
      position: sticky;
      z-index: 10;
      /* right: 5px; */
      top: 52px;
      /* will-change: top; */
      padding-top: 20px;
      /* width: 29.7%; */
      height: calc(100vh - 52px);
        /* 95vh = a rough approximation for most cases */
      overflow-y: auto; /* TODO: scrollbar ends look weird without "caps" */
      overflow-x: hidden;
    }
    .details-bar .btn {
      width: 100%;
      margin-bottom: 20px;
    }
    @media screen and (max-width: 768px)
    {
      .details-bar {
        right: 10px;
        width: 300px;
        overflow-y: hidden;
        max-width: 100%;
      }
      .details-bar:not(.in) {
        max-height: 55px;
      }
      .details-bar .contents  {
        display: none;
      }
      .details-bar.in .contents {
        display: block;
      }
    }
    .defocus {
      filter: blur(5px);
    }
    .backdrop {
      position: absolute;
      top: -15px;
      left: 0;
      width: 100%;
      height: 120%;
      background: rgba(0,0,0,0.2);
    }`,
    `
    .intake-header {
      position: relative;
      margin-top: 5px;
    }
    .subject-timestamp {
      position: absolute;
      bottom: 0px;
      right: 0px;
    }
    .menu-button {
      display: block;
      position: absolute;
      font-size: 18px;
      left: -40px;
      top: 0px;
      width: 20px;
      height: 100%;
      text-align: center;
      opacity: 0;
      transition: opacity 0.4s;
    }
    .intake-header.has-comment .glyphicon-comment {
      color: #3c763d; /* text-success color TODO: non-magic ref? */
    }
    .intake-header:hover .menu-button,
    .intake-header.has-comment .menu-button {
      opacity: 1;
    }
    .menu {
      max-height: 0px;
      opacity: 0;
      overflow-y: hidden;
      transition: max-height 0.8s, opacity 0.8s;
      padding-left: 1em;
      border-left: 1px solid gray;
    }
    .menu.open {
      max-height: 1600px;
      opacity: 1;
	}
	.end-buttons {
		margin-top: 2em;
  }
  .end-buttons 
  .quick-action-button {
    display: inline-block;
    width: 49%;
    vertical-align: top;
  }
  .quick-action-button select {
    margin-bottom: 20px;
  }
  .btn .glyphicon {
    line-height: 20px;
  }
  `
  ],
  providers: []
})
export class IntakeFormComponent implements SaveableForm, OnInit, OnDestroy
{
  public useSidebar : boolean = false;
  public useReturnHome : boolean = true;
  public useFCWIButtons_DEBUG : boolean = false;

  public objectId : number = -1;

  public title : string;

  public sidebarOpen : boolean = false;

  public algorithm : RunningAlgorithm;

  /**
   * The form is the root container for all intake step controls.
   * @type {FormGroup}
   */
  public form : FormGroup;

  private paramNavSub : Subscription = new Subscription();
  private paramNavWindow : number = 1000;

  public algSwitcher : FormControl = new FormControl();

  /**
   * Construct a new {@link IntakeFormComponent}.
   */
  public constructor (
    private router : Router,
    private route : ActivatedRoute,
    private messaging : MessagingService,
    public subjects : SubjectService,
    private intakeSteps : IntakeStepService,
    private reportSteps : ReportStepService,
    private comments : CommentService,
    private alerts : AlertService,
    private modals : ModalService,
    private watchlist : WatchlistService,
    public algorithms : AlgorithmService,
    private users : UserService,
    private element : ElementRef,
    private settings : SettingService,
    private search : SearchService
  )
  {
    // TODO: If we switch to allowing a no-interstitial-page transition, we'll
    // need a different event model (or router changes) so that IFC→IFC triggers
    // correct initialization. Subscribing to params alone does NOT suffice
    // because param changes occur before navigation success (so they could
    // represent a navigation doomed to fail).
    // this.route.params.subscribe((params : any) => { /* */ } );
  }

  public ngOnInit () : void
  {
    this.algorithms.lock(this);
    this.algorithm = this.algorithms.running;
    this.title =
      this.algorithm.displayName || "Intake Form";
    this.form = new FormGroup({});

    console.log("▼▼▼▼▼ form initialized ▼▼▼▼▼▼");
    (window as any).intake = this.form;
    console.info(" (available as window.intake) ");

    // this.context.registerManager(this);
    this.objectId = this.algorithms.running.subject.id;

    this.resummarizeIfWatched();

    this.algSwitcher.reset();

    // TODO: Might be better way to ensure this reset?
    window.scrollTo(0, 0);

    // TODO: guest faking
    this.useSidebar =
      ["a-tlc", "a-first-check-wi"].indexOf(this.algorithm.name) < 0;
    this.useReturnHome =
      ["a-tlc"].indexOf(this.algorithm.name) >= 0;
    this.useFCWIButtons_DEBUG =
      "a-first-check-wi" === this.algorithm.name;
  }

  public ngOnDestroy () : void
  {
    console.log("▲▲▲▲▲ form destroyed ▲▲▲▲▲");
  }

  /**
   * Determine if the UI components are ready to be destroyed or not, generally
   * based on a user confirmation of their dirtyness / lost data.
   * @return {Observable<boolean>}
   */
  public canLeave () : Observable<boolean>
  {
    return this.canAbandonSubject();
  }

  /**
   * The actual implementation of canLeave: if there are dirty steps, then
   * present a dialog either about missing required steps or about needing to
   * save in order to preserve the changes.
   * @return {Observable<boolean>}
   */
  public canAbandonSubject () : Observable<boolean>
  {
    if (this.form.dirty || this.comments.changesSinceSave)
    {
      if (!this.requiredStepsComplete())
      {
        this.modals.confirm(LEAVING_UNSAVABLE_MODAL).map(
          (stay : boolean) => !stay);
      }

      const dirties : Array<[string, AbstractControl]> =
        Object.entries(this.form.controls).filter(
          ([name, ctrl] : [string, AbstractControl]) => ctrl.dirty);
      // Form is dirty and needs save!
      // NOTE: The "dismiss" case is actually the one that indicates we
      // continue unconditionally (because we don't care about saving).
      return this.modals.confirm(
        SAVE_NEEDED_MODAL(dirties, this.comments.changesSinceSave)
      ).flatMap((saveCommand : boolean) =>
        saveCommand
          ? this.saveSubject()
          : Observable.of(false)
      ).defaultIfEmpty(true);
    }

    if (this.comments.hasUnsubmittedContent())
    {
      this.modals.confirm(INCOMPLETE_COMMENTS_MODAL)
        .map((stay : boolean) => !stay);
    }

    console.info("leaving SC: no dirty content, so no save");
    return Observable.of(true);
  }

  /**
   * Determine if the algorithm's required steps have data.
   * @return {boolean}
   */
  private requiredStepsComplete () : boolean
  {
    return true;
    // return !anyChildHasError(this.form, "required");
  }

  public saveRequested : boolean = false;
  public saveRequestTime : number = -1;

  /**
   * Attempt a user-prompted save operation
   * @return {Observable<boolean>}
   */
  public trySaveSubject (depart : boolean = false) : Observable<boolean>
  {
    if (!this.requiredStepsComplete())
    {
      // The required dialog is just a warning and doesn't provide any
      // immediate action, so we can simply return the ready state and always
      // fail an attempted save when the required fields are missing.
      this.modals.alert(REQUIRED_STEPS_MODAL);
      return Observable.empty<boolean>();
    }
    this.saveRequested = true;
    this.saveRequestTime = Date.now();
    const saveResult : Observable<boolean> = this.saveSubject();
    saveResult.subscribe((saved : boolean) => {
      if (saved)
      {
        this.form.markAsPristine();
        this.comments.resetChangeFlag();
        this.alerts.success("The record has been saved.");
        this.saveRequested = false;
        if (depart)
        {
          this.router.navigate(["/home"]);
        }
      }
    });
    return saveResult;
  }

  /**
   * Ask the subject context to save itself (by communicating with the server).
   * @return {Observable<boolean>}
   */
  private saveSubject (force : boolean = false) : Observable<boolean>
  {
    return this.algorithm.save(force).flatMap(
      (code : ResponseCode) => {
        if (code === ResponseCode.OK)
        {
          const ref : SubjectReference =
            this.algorithm.subject.findRef("watch");
          if (!ref)
          {
            const autoWatch : boolean =
              this.settings.get("subscribe after save");
            if (autoWatch)
            {
              this.watchlist.watchRunningSubject().subscribe();
            }

          }
          else
          {
            this.resummarize(ref.id);
          }
          return [true];
        }
        else if (code === ResponseCode.Irretrievable)
        {
          this.modals.confirm(RETRIEVABILITY_MODAL)
            .flatMap((saveHard : boolean) => {
              if (saveHard)
              {
                // Note that we are recursing here, so we're reliant upon the
                // fact that the server should NEVER fail with Irretrievable
                // after a forcible save to prevent a loop.
                return this.saveSubject(true);
              }
              return [false];
          });
        }
        else
        {
          this.alerts.warn(
            "The subject could not be saved: " + GENERIC_REASON_TEXT.get(code));
          return [false];
        }
      }
    );
  }

  public tryDuplicate () : void
  {
    this.subjects.launchDuplicate(this.algorithm.algorithm);
  }

  private resummarize (watchId : number) : void
  {
    this.watchlist.resummarize({watchId}).subscribe();
  }

  private resummarizeIfWatched () : void
  {
    // TODO: revisit this flow. Can subjects resummarize themselves?
    const ref : {id : number} =
      this.algorithm.subject.findRef("watch");
    if (ref)
    {
      this.resummarize(ref.id);
    }
  }

  /**
   * Unload is run before the page is closed and checks if there are any unsaved
   * changes, triggering a browser-controlled stay/leave dialog if so.
   * @param  {BeforeUnloadEvent} event
   */
  @HostListener("window:beforeunload", ["$event"])
  public unload (event : BeforeUnloadEvent) : void
  {
    if ((this.form.dirty || this.comments.changesSinceSave))
    {
      // most browsers ignore this message nowadays (due to the prevalence of
      // social engineering attacks on tab closure), but it's still part of the
      // spec and could return one day *shrug*
      event.returnValue = BROWSER_SAVE_NEEDED_MESSAGE;
      return;
    }
  }
  // TODO: This manual animation might get a small performance boost from being
  // moved outside of the Angular zone system? (though it's risky for errors)

  /**
   * The sidebarRef is a reference to the ng-wrapped DOM element for the intake
   * form sidebar (containing chat/reports/etc).
   * @type{ElementRef}
   */
  @ViewChild("sidebar") private sidebarRef : ElementRef;

  /**
   * Has the scroll listener already reacted to an event during this frame?
   * @type {boolean}
   */
  private scrollUpdating : boolean = false;

  /**
   * Listen for scroll events and use them to face
   * @param  {UIEvent} event
   */
  // @HostListener("window:scroll", ["$event"])
  public scroll (event : UIEvent) : void
  {
    if (!this.scrollUpdating && this.useSidebar)
    {
      // Determine how thick the navbar effectively is at this scroll position.
      const headerHeight : number = Math.max(0, 52 - window.scrollY);
      const stickyTop : string = headerHeight + "px";
      const stickyHeight : string = "calc(100vh - " + headerHeight + "px)";
      if (this.sidebarRef.nativeElement.style.top !== stickyHeight)
      {
        this.scrollUpdating = true;
        this.sidebarRef.nativeElement.style.top = stickyTop;
        this.sidebarRef.nativeElement.style.height = stickyHeight;
        // Don't allow another scroll update until the next animation frame.
        // This prevents the update from firing multiple times per frame, which
        // is very, very possible with how often scroll events can be emitted.
        window.requestAnimationFrame(
          () => this.scrollUpdating = false);
      }
    }
  }

  public save () : Observable<boolean>
  {
    throw new Error("UNIMPLEMENTED - PARTIAL INTERFACE COMPLIANCES");
  }

  public showLinkedReminderModal () : void
  {
    this.modals.observe<ReminderModalResult>(
      AddReminderModalComponent,
      {size: "md", hasImpliedLink: true}
    ).subscribe((r : ReminderModalResult | null) => {
      if (r)
      {
        this.watchlist.addReminder(
          r.summary,
          r.triggerTime,
          r.linkedRecord ? this.objectId : undefined
        ).subscribe();
      }
    });
  }

  public actionSelection : FormControl = new FormControl(null);
  public advancedAction (e : Event & {target : HTMLSelectElement}) : void
  {
    // debugger;
    switch (e.target.value)
    {
      case "refer":
        this.openReferralModal();
        break;
      case "export":
        this.openExportModal();
        break;
      case "import":
        this.openImportModal();
        break;
      default:
        console.error(`Unknown action from target`, e.target);
    }
    e.target.value = null;
  }

  public openReferralModal () : void
  {
    this.modals.observe(ReferralModalComponent)
      .flatMap((referralMsg : string) => {
        if (!referralMsg)
        {
          return [false];
        }
        return this.messaging.resultOf(new ReferSubjectContextRequest(
          this.algorithm.subject.id,
          referralMsg
        ));
      }).subscribe((referred : boolean) => {
        if (referred)
        {
          this.alerts.success("The subject has been referred!");
        }
      });
  }

  public openExportModal () : void
  {
    this.modals.display(
      ExportModalComponent,
      {
        steps: this.intakeSteps.all(),
        algorithm: this.algorithm.name,
        type: this.algorithm.subject.type
      }
    );
  }

  public openImportModal () : void
  {
    alert("Error: you don't have permission to perform that action!");
  }

  @HostListener("document:keydown.control.s", ["$event"])
  @HostListener("document:keydown.meta.s", ["$event"])
  public saveFromKeyPress (
    ev : Event & {submitFirst? : [AnswerProxy, IntakeStep]}
  ) : void
  {
    if (document.activeElement instanceof HTMLElement)
    {
      document.activeElement.blur();
      requestAnimationFrame(() => this.trySaveSubject());
    }
    else
    {
      this.trySaveSubject();
    }
    ev.preventDefault();
  }

  @ViewChild(ChatPanelComponent) private chatRef : ChatPanelComponent;

  @HostListener("document:keydown.control.Enter")
  @HostListener("document:keydown.meta.Enter")
  public focusChat () : void
  {
    this.chatRef.externalFocalize();
  }

  public scrollToTop () : void
  {
    document.scrollingElement.scrollTo({top: 0});
  }

  public switchToAlg () : void
  {
    if (this.algSwitcher.value)
    {
      this.algorithms.open(
        this.algSwitcher.value, this.algorithm.subject);
    }
  }

  public openNextSearchResult () : void
  {
    if (this.algorithm && this.algorithm.subject)
    {
      const searchRef : SubjectReference =
        this.algorithm.subject.findRef("search");
      const i : number = this.search.results.findIndex(
        (r : Result) => r.recordId === searchRef.id);
      if (i > -1)
      {
        if (i === this.search.results.length - 1)
        {
          return alert("You are already at the last search result.");
        }
        const target : Algorithm | null =
          this.algorithms.defaultForOpen(this.algorithm.subject.type);
        this.search.open(this.search.results[i + 1], true, target).subscribe();
        return;
      }
    }
    alert("Could not find the next search result!" +
      "DEVELOPMENT: Check the result identifier set-up.");
  }

  public substitutionMacro(e : KeyboardEvent) : void
  {
    if (e.key === ">")
    {
      if (document.activeElement instanceof HTMLTextAreaElement)
      {
        let pos = document.activeElement.selectionStart;
        let str = document.activeElement.value;
        console.error(`[${pos}] ${str}`);
        console.error(/[^ ]*>/.exec(str.substr(0, pos)));
      }
    }
  }

  public printPage () : void
  {
    console.log(window);
    window.print();
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/intake-form/intake-form.component.ts